/** Logistics Prediction and Routing. Mirrors tma-be `src/prediction`. */

export enum PredictionStatus {
  PREDICTED = "predicted",
  NOT_PREDICTABLE = "not_predictable",
}

export enum NotPredictableReason {
  VISIT_GAP_NOT_SET = "visit_gap_not_set",
  NO_COORDINATES = "no_coordinates",
  /** No longer produced; kept to mirror the backend enum. */
  NO_SITE = "no_site",
  NO_DELIVERIES_NO_TARGET = "no_deliveries_no_target",
  NO_DELIVERIES_TARGET_DECLARED = "no_deliveries_target_declared",
  TOO_FEW_DELIVERIES_NO_TARGET = "too_few_deliveries_no_target",
}

export enum PlanningType {
  DEFINED = "defined",
  FORECASTED = "forecasted",
  MIXED = "mixed",
}

export enum AdviceStatus {
  OVER = "over",
  UNDER = "under",
  OFF_PACE = "off_pace",
  UNTRACKED = "untracked",
  MISSING = "missing",
  ON_TARGET = "on_target",
}

export enum PredictionOutcome {
  PENDING = "pending",
  EARLY = "early",
  ON_TIME = "on_time",
  LATE = "late",
}

export enum FeedbackReason {
  WRONG_DATE = "wrong_date",
  WRONG_QUANTITY = "wrong_quantity",
  WRONG_CATEGORY = "wrong_category",
  WRONG_TRUCK = "wrong_truck",
  OTHER = "other",
}

export interface PredictionLine {
  productId: number;
  quantity: number;
  source: "history" | "target";
  visits: number;
  share: number;
  medianQuantity: number | null;
  spreadQuantity: number | null;
  rhythmDays: number | null;
  spreadDays: number | null;
  cageShare: number | null;
  target: { count: number; days: number } | null;
  advice: AdviceStatus[];
}

export interface IrregularCategory {
  productId: number;
  visits: number;
  share: number;
  medianQuantity: number | null;
  spreadQuantity: number | null;
  reason: "rare" | "unstable";
}

export interface ActualLine {
  productId: number;
  quantity: number;
}

export interface PredictionSubClient {
  id: number;
  name: string;
  address: string;
  latitude: string | number | null;
  longitude: string | number | null;
  /** The Site set on the sub-client page: its own side, shown next to "To". */
  site?: { id: number; name: string } | null;
  contactPersonName: string | null;
  contactPersonPhone: string | null;
  contactPersonEmail: string | null;
}

export interface LogisticsPrediction {
  id: number;
  /** Only on the detail response. */
  client?: { id: number };
  subClient: PredictionSubClient;
  isCurrent: boolean;
  status: PredictionStatus;
  reason: NotPredictableReason | null;
  visits: number;
  outliers: number;
  lastVisitAt: string | null;
  /** Decimal columns arrive as strings. */
  rhythmDays: string | number | null;
  spreadDays: string | number | null;
  dueFrom: string | null;
  dueTo: string | null;
  /** The raw predicted day (centre of the window); the routing's 10 % range is centred on it. */
  predictedAt: string | null;
  /** The gap the 10 % range is taken from (rhythm, or the target's days). */
  gapDays: string | number | null;
  quietAfter: string | null;
  planningType: PlanningType | null;
  contentUnknown: boolean;
  lines: PredictionLine[];
  irregular: IrregularCategory[];
  computedAt: string;
  refreshedAt: string;
  outcome: PredictionOutcome;
  actualVisitAt: string | null;
  actualLines: ActualLine[] | null;
}

// ── Live shipment plan (mirrors `shipment-planner.service.ts`) ──

export interface LoadPick {
  itemId: number;
  externalId: string | null;
  itemType: "item" | "cage" | "cage_item";
  units: number;
  cageItemIds: number[];
  lastScanAt: string | null;
}

export interface SkippedCage {
  itemId: number;
  externalId: string | null;
  units: number;
  lastScanAt: string | null;
}

export interface CategoryLoad {
  productId: number;
  requested: number;
  picked: number;
  picks: LoadPick[];
  skippedCages: SkippedCage[];
  available: number;
}

export interface StopDrop extends CategoryLoad {
  m3: number | null;
  kg: number | null;
}

export interface MissingData {
  kind: "vehicle_model" | "category";
  id: number;
  name: string;
  fields: string[];
}

export interface DriverInfo {
  id: number;
  name: string;
  /** "HH:mm" */
  startTime: string;
  endTime: string;
  /** ISO weekdays, 1 = Monday … 7 = Sunday. */
  workingDays: number[];
}

export interface TruckOption {
  vehicleId: number;
  name: string;
  modelId: number;
  modelName: string;
  siteId: number | null;
  siteName: string | null;
  capacityM3: number | null;
  payloadKg: number | null;
  drivers: DriverInfo[];
  fitsAll: boolean | null;
}

export interface Leg {
  minutes: number | null;
  km: number;
  source: "google" | "straight_line";
}

export interface RoutingAvailability {
  available: boolean;
  reason: "ok" | "no_api_key" | "api_error";
  message: string | null;
}

export interface ShipmentStopPlan {
  order: number;
  predictionId: number;
  subClientId: number;
  name: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  gapDays: number;
  legFromPrevious: Leg | null;
  drops: StopDrop[];
  units: number;
  m3: number | null;
  kg: number | null;
  tripIndex: number | null;
}

export type TripReason = "fits" | "exceeds_truck" | "no_driver" | "no_day_found" | "exceeds_day";

export interface TripLeg {
  order: number;
  minutes: number | null;
  km: number;
  arriveAt: string | null;
  leaveAt: string | null;
}

export interface ShipmentTripPlan {
  index: number;
  truck: TruckOption;
  driver: DriverInfo | null;
  day: string;
  dayOffset: number;
  stopOrders: number[];
  legs: TripLeg[];
  returnLeg: { minutes: number | null; km: number };
  drivingMinutes: number | null;
  distanceKm: number;
  offloadingMinutes: number;
  totalMinutes: number | null;
  departAt: string | null;
  returnAt: string | null;
  units: number;
  m3: number | null;
  kg: number | null;
  fits: boolean | null;
  runs: number;
  reason: TripReason;
}

export type ShipmentWarning =
  | "no_site"
  | "no_truck"
  | "missing_data"
  | "short_stock"
  | "exceeds_truck"
  | "no_driver"
  | "spills_over_days"
  | "exceeds_day"
  | "no_road_times"
  | "stop_without_coordinates";

export interface LoadOrigin {
  siteId: number;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

export interface LoadTotal {
  units: number;
  m3: number | null;
  kg: number | null;
}

export interface ShipmentPlan {
  origin: LoadOrigin | null;
  originReason: "full_load" | "most_stock" | "no_site";
  originToFirstStopKm: number | null;
  stops: ShipmentStopPlan[];
  trips: ShipmentTripPlan[];
  trucks: TruckOption[];
  categories: CategoryLoad[];
  total: LoadTotal;
  missing: MissingData[];
  routing: RoutingAvailability;
  days: number;
  warnings: ShipmentWarning[];
}

// ── Stored shipments ──

export interface ShipmentStopSummary {
  predictionId: number;
  subClientId: number;
  rangeFrom: string;
  rangeTo: string;
  gapDays: number;
}

export interface ShipmentSummary {
  id: number;
  shipmentDate: string;
  isCurrent: boolean;
  anchorPredictionId: number;
  groupingLimitMinutes: number;
  roadTimesUsed: boolean;
  computedAt: string;
  refreshedAt: string;
  /** Thumbs up / down on the shipment as a whole (moved from the prediction 2026-10-01). */
  feedbackUseful: boolean | null;
  feedbackReason: FeedbackReason | null;
  feedbackComment: string | null;
  feedbackBy: { id: number; name: string; email: string } | null;
  feedbackAt: string | null;
  stops: ShipmentStopSummary[];
}

export interface ShipmentCard {
  shipment: ShipmentSummary;
  predictions: LogisticsPrediction[];
  plan: ShipmentPlan | null;
}

export interface VisitLine {
  productId: number;
  quantity: number;
  cageIds: number[];
  loose: number;
}

export interface Visit {
  startAt: string;
  endAt: string;
  lines: VisitLine[];
  quantity: number;
}

// ── API shapes ──

export interface PredictionSettings {
  visitGapMinutes: number;
  minDeliveries: number;
  regularSharePercent: number;
  maxSpreadPercent: number;
  geofenceRadius: number;
  groupingLimitMinutes: number;
  offloadingMinutes: number;
}

export interface PredictionBoard {
  asOf: string | null;
  settings: PredictionSettings;
  routing: RoutingAvailability;
  subClients: { id: number; name: string }[];
  categories: { id: number; name: string; color: string | null }[];
  /** Current shipments dated today or later, earliest first. */
  shipments: ShipmentCard[];
  notPredictable: LogisticsPrediction[];
}

export interface PredictionDetail {
  prediction: LogisticsPrediction;
  visits: Visit[];
  /** The current shipment this prediction is a stop of, if any. */
  shipment: ShipmentSummary | null;
}

export interface ShipmentDetail extends ShipmentCard {
  /** Visits behind every stop, keyed by prediction id. */
  visits: Record<number, Visit[]>;
}

export interface PredictionFeedbackPayload {
  useful: boolean;
  reason?: FeedbackReason;
  comment?: string;
}

export interface FeedbackSummary {
  useful: number;
  notUseful: number;
  reasons: Record<FeedbackReason, number>;
}

export interface ShipmentComputeSummary {
  predictions: number;
  shipments: number;
  grouped: number;
  created: number;
  refreshed: number;
  closed: number;
  roadTimesUsed: boolean;
}

export interface PredictionComputeSummary {
  subClients: number;
  predicted: number;
  notPredictable: number;
  created: number;
  refreshed: number;
  outcomes: number;
  shipments: ShipmentComputeSummary;
  routing: RoutingAvailability;
}

/**
 * The settings a TMA admin edits on the tab. The two category cut-offs are
 * not exposed. Grouping limit and off-loading time are stored in minutes.
 */
export interface PredictionConfig {
  predictionVisitGapMinutes: number;
  predictionMinDeliveries: number;
  predictionGroupingLimitMinutes: number;
  predictionOffloadingMinutes: number;
}
